import axiosInst from '@/utils/axiosinstance';
import { UAParser } from 'ua-parser-js';

/**
 * Fire-and-forget page-visit tracking for the admin Users report.
 *
 * Records the visit into the collection behind the matching admin tab:
 *   - logged-in user  → POST /api/adminreport  → "Authenticated Users" tab
 *   - guest           → POST /api/visitors     → "Unauthenticated Users" tab
 *
 * This is the report-level counterpart to trackInteraction('page-view', …),
 * which logs every navigation into interactionEvents for the Interaction
 * Analytics page. Each call here costs a server-side ip-api location lookup,
 * so wire it into specific page mounts (Dashboard, View Ads, Messages, …),
 * NOT into a global router hook. Never throws and never blocks the page.
 *
 * Usage:
 *   import { trackPageVisit } from '@/utils/trackPageVisit';
 *   trackPageVisit('Messages');
 *   trackPageVisit('View Ads', { serviceSlug: this.serviceName, servicesID: this.servicesID });
 *
 * @param {string} pageName  Label shown in the admin tab's Page column.
 * @param {{serviceSlug?: string, servicesID?: string}} [opts]  Listing identity,
 *   only meaningful for listing detail views (links the row to /view-post/:slug).
 */
export async function trackPageVisit(pageName, opts = {}) {
  try {
    const parser = new UAParser();
    const browserInfo = parser.getResult();

    const ipRes = await fetch('https://api64.ipify.org?format=json');
    const { ip } = await ipRes.json();

    const visitData = {
      ip,
      timestamp: new Date().toISOString(),
      pageName,
      browser: browserInfo.browser.name,
      browserVersion: browserInfo.browser.version,
      os: browserInfo.os.name,
      osVersion: browserInfo.os.version,
      device: browserInfo.device.type || 'Desktop',
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    const userID = localStorage.getItem('userid');
    if (userID) {
      await axiosInst.post('/api/adminreport', {
        ...visitData,
        userID,
        userName: localStorage.getItem('username') || '',
      });
    } else {
      await axiosInst.post('/api/visitors', {
        ...visitData,
        serviceSlug: opts.serviceSlug || null,
        servicesID: opts.servicesID || null,
      });
    }
  } catch (_) {
    /* tracking must never surface an error */
  }
}

export default trackPageVisit;
